import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export type IconName =
  | 'home'
  | 'courses'
  | 'catalog'
  | 'orgs'
  | 'profile'
  | 'activity'
  | 'referral'
  | 'billing'
  | 'settings'
  | 'logout'
  | 'users'
  | 'services'
  | 'roles'
  | 'groups'
  | 'bell'
  | 'teach'
  | 'analytics'
  | 'admin'
  | 'calendar'
  | 'briefcase'
  | 'certificate'
  | 'audit'
  | 'enroll'
  | 'orders'
  | 'payments'
  | 'promo'
  | 'programs'
  | 'review'
  | 'contract'
  | 'recruit'
  | 'palette'
  | 'shield'
  | 'key'
  | 'lock'
  | 'chat';

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="block h-full w-full"
      aria-hidden
    >
      {children}
    </svg>
  );
}

const GLYPHS: Record<IconName, ReactNode> = {
  home: (
    <Svg>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 10v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
    </Svg>
  ),
  courses: (
    <Svg>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v15H6.5A2.5 2.5 0 0 0 4 19.5V4.5A2.5 2.5 0 0 1 6.5 2z" />
    </Svg>
  ),
  catalog: (
    <Svg>
      <rect x="3" y="3" width="7" height="7" rx="1.2" />
      <rect x="14" y="3" width="7" height="7" rx="1.2" />
      <rect x="3" y="14" width="7" height="7" rx="1.2" />
      <rect x="14" y="14" width="7" height="7" rx="1.2" />
    </Svg>
  ),
  orgs: (
    <Svg>
      <path d="M6 22V8l6-4 6 4v14" />
      <path d="M10 22v-6h4v6" />
    </Svg>
  ),
  profile: (
    <Svg>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5 20c1.2-3.2 3.8-5 7-5s5.8 1.8 7 5" />
    </Svg>
  ),
  activity: (
    <Svg>
      <path d="M3 12h4l2.5-7 5 14L17 12h4" />
    </Svg>
  ),
  referral: (
    <Svg>
      <path d="M12 7v13" />
      <path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8" />
      <path d="M12 7a3 3 0 1 0-3-3c0 1.8 3 3 3 3z" />
      <path d="M12 7a3 3 0 1 1 3-3c0 1.8-3 3-3 3z" />
    </Svg>
  ),
  billing: (
    <Svg>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h4" />
    </Svg>
  ),
  settings: (
    <Svg>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.2M12 18.8V21M4.9 6.3l1.6 1.6M17.5 16.1l1.6 1.6M3 12h2.2M18.8 12H21M4.9 17.7l1.6-1.6M17.5 7.9l1.6-1.6" />
    </Svg>
  ),
  logout: (
    <Svg>
      <path d="M10 7V5a2 2 0 0 1 2-2h7v18h-7a2 2 0 0 1-2-2v-2" />
      <path d="M15 12H3m0 0 3-3M3 12l3 3" />
    </Svg>
  ),
  users: (
    <Svg>
      <circle cx="9" cy="8" r="2.6" />
      <circle cx="16.5" cy="8.5" r="2.1" />
      <path d="M3.8 19c.9-3 3.2-4.6 5.2-4.6s4.3 1.6 5.2 4.6" />
      <path d="M14.2 14.8c1.5 0 3.2 1 4 3.2" />
    </Svg>
  ),
  services: (
    <Svg>
      <path d="M12 3 4 7.5 12 12l8-4.5L12 3z" />
      <path d="M4 12.5 12 17l8-4.5" />
      <path d="M4 16.5 12 21l8-4.5" />
    </Svg>
  ),
  roles: (
    <Svg>
      <path d="M12 3 5 6v6c0 4.2 3 7.2 7 8.5 4-1.3 7-4.3 7-8.5V6l-7-3z" />
    </Svg>
  ),
  groups: (
    <Svg>
      <circle cx="12" cy="8" r="3" />
      <path d="M5 20c1.1-3.4 3.6-5 7-5s5.9 1.6 7 5" />
    </Svg>
  ),
  bell: (
    <Svg>
      <path d="M6 9a6 6 0 1 1 12 0c0 7 2 7 2 9H4c0-2 2-2 2-9" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </Svg>
  ),
  teach: (
    <Svg>
      <path d="M22 10 12 5 2 10l10 5 10-5z" />
      <path d="M6 12v5c2 1.8 4 2.6 6 2.6s4-.8 6-2.6v-5" />
    </Svg>
  ),
  analytics: (
    <Svg>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20v-4" />
    </Svg>
  ),
  admin: (
    <Svg>
      <circle cx="12" cy="12" r="3" />
      <path d="M4 12h3M17 12h3M12 4v3M12 17v3" />
    </Svg>
  ),
  calendar: (
    <Svg>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </Svg>
  ),
  briefcase: (
    <Svg>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M8 8V6a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
  ),
  certificate: (
    <Svg>
      <circle cx="12" cy="9" r="5" />
      <path d="M8.5 13.5 7 21l5-2 5 2-1.5-7.5" />
    </Svg>
  ),
  audit: (
    <Svg>
      <path d="M8 4h9a2 2 0 0 1 2 2v14H8a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z" />
      <path d="M10 10h6M10 14h4" />
    </Svg>
  ),
  enroll: (
    <Svg>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20c1-3.2 3.4-5 5.5-5s4.5 1.8 5.5 5" />
      <path d="M19 8v6M16 11h6" />
    </Svg>
  ),
  orders: (
    <Svg>
      <path d="M6 7h15l-1.5 9H8L6 7z" />
      <path d="M6 7 5 4H2" />
      <circle cx="9" cy="20" r="1.3" />
      <circle cx="17" cy="20" r="1.3" />
    </Svg>
  ),
  payments: (
    <Svg>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <circle cx="12" cy="12.5" r="2.4" />
    </Svg>
  ),
  promo: (
    <Svg>
      <path d="M4 10.5 10.5 4H20v9.5L13.5 20H4V10.5z" />
      <circle cx="15" cy="9" r="1.2" />
    </Svg>
  ),
  programs: (
    <Svg>
      <path d="M4 6h7v14H6a2 2 0 0 1-2-2V6z" />
      <path d="M13 6h7v12a2 2 0 0 1-2 2h-5V6z" />
    </Svg>
  ),
  review: (
    <Svg>
      <path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l1-4.2A8 8 0 1 1 21 12z" />
      <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
    </Svg>
  ),
  contract: (
    <Svg>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9l-5-6z" />
      <path d="M14 3v6h6M9 13h6M9 17h4" />
    </Svg>
  ),
  recruit: (
    <Svg>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20c1-3.2 3.4-5 5.5-5s4.5 1.8 5.5 5" />
      <path d="M19 8v6M16 11h6" />
    </Svg>
  ),
  palette: (
    <Svg>
      <path d="M12 3a9 9 0 1 0 0 18h.5a2.5 2.5 0 0 0 0-5H14a9 9 0 0 0-2-13z" />
      <circle cx="8" cy="10" r="0.8" fill="currentColor" />
      <circle cx="10.5" cy="7.2" r="0.8" fill="currentColor" />
      <circle cx="14.5" cy="7.8" r="0.8" fill="currentColor" />
    </Svg>
  ),
  shield: (
    <Svg>
      <path d="M12 3 5 6v6c0 4.2 3 7.2 7 8.5 4-1.3 7-4.3 7-8.5V6l-7-3z" />
    </Svg>
  ),
  key: (
    <Svg>
      <circle cx="8" cy="15" r="3.2" />
      <path d="M10.5 13.5 20 4l2 2-2 2-2-1-2 2-1-1-2 2" />
    </Svg>
  ),
  lock: (
    <Svg>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
  chat: (
    <Svg>
      <path d="M21 12a8 8 0 0 1-11.5 7.2L4 20l1-4.2A8 8 0 1 1 21 12z" />
    </Svg>
  ),
};

const SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-5 w-5',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

type Props = {
  name: IconName;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

export function AppIcon({ name, size = 'md', className }: Props) {
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center leading-none text-current', SIZE[size], className)}
      aria-hidden
    >
      {GLYPHS[name]}
    </span>
  );
}

export const SCENARIO_ICON: Record<string, IconName> = {
  study: 'courses',
  catalog: 'catalog',
  reviews: 'review',
  'teach-assign': 'teach',
  'teach-review': 'review',
  'mgr-recruit': 'recruit',
  'mgr-contract': 'contract',
  'mgr-cert': 'certificate',
  'admin-schedule': 'calendar',
  'admin-enroll': 'enroll',
};
